import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Users,
  FolderKanban,
  Activity,
  ArrowUpRight,
  MoreHorizontal,
  FileText,
  DollarSign,
  Clock,
} from 'lucide-react';
import Card3D from './Card3D.js';
import AnimatedNumber from './AnimatedNumber.js';
import useIntro from './useIntro.js';

function PesoSign({ size = 20, style, ...rest }) {
  return (
    <span
      {...rest}
      style={{
        fontSize: typeof size === 'number' ? size : 20,
        lineHeight: 1,
        fontWeight: 800,
        ...(style || {}),
      }}
    >
      ₱
    </span>
  );
}

const toneColor = {
  pos: 'var(--nad-pos)',
  info: 'var(--nad-info)',
  accent: 'var(--nad-accent-txt)',
  neg: 'var(--nad-neg)',
};

const formatPeso = (n) => {
  if (n == null || isNaN(n)) return '₱0';
  return '₱' + Number(n).toLocaleString('en-PH', { maximumFractionDigits: 0 });
};

export default function DashboardPage({ axiosInstance }) {
  const isIntro = useIntro();
  const stagger = (i) =>
    isIntro
      ? { className: 'stagger-in', style: { '--i': i } }
      : { className: '', style: undefined };

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      // Global deadman: if any of the parallel endpoints hangs (TCP half-open,
      // 520/522 silent RST, etc.) we unconditionally unlock loading after 25s
      // and fall through to the built-in demo/fallback stats at the bottom.
      let deadFired = false;
      const deadline = setTimeout(() => {
        deadFired = true;
        if (mounted) setLoading(false);
      }, 25000);

      try {
        const endpoints = [
          axiosInstance?.get('/dashboard/stats').catch(() => ({ data: null })),
          axiosInstance?.get('/attendance/dashboard-stats').catch(() => ({ data: null })),
          axiosInstance?.get('/payroll/dashboard/stats').catch(() => ({ data: null })),
          axiosInstance?.get('/employees').catch(() => ({ data: [] })),
          axiosInstance?.get('/admins').catch(() => ({ data: [] })),
          axiosInstance?.get('/projects').catch(() => ({ data: [] })),
          axiosInstance?.get('/ccp/project-details').catch(() => ({ data: [] })),
        ];

        const results = await Promise.all(endpoints);
        if (!mounted || deadFired) return;

        const [
          dashStats,
          attendStats,
          payrollStats,
          employees,
          admins,
          projectsRes,
          ccpProjects,
        ] = results;

        const totalEmployees = Array.isArray(employees?.data) ? employees.data.length : 0;
        const totalAdmins = Array.isArray(admins?.data) ? admins.data.length : 0;
        const totalProjects =
          (Array.isArray(projectsRes?.data) ? projectsRes.data.length : 0) +
          (Array.isArray(ccpProjects?.data) ? ccpProjects.data.length : 0);

        const totalRevenue =
          payrollStats?.data?.total_gross_pay ??
          dashStats?.data?.total_revenue ??
          2847500;

        const presentCount = attendStats?.data?.present_today ??
          dashStats?.data?.employees_present ??
          Math.max(1, Math.floor((totalEmployees + totalAdmins) * 0.85));

        const totalTeam = totalEmployees + totalAdmins || 156;

        const liveStats = [
          {
            label: 'Total Revenue',
            value: formatPeso(totalRevenue),
            change: payrollStats?.data?.change_pct ?? dashStats?.data?.revenue_change ?? '+12.5%',
            trending: !payrollStats?.data?.change_pct || !String(payrollStats.data.change_pct).startsWith('-') ? 'up' : 'down',
            icon: PesoSign,
            color: 'from-accent-400/20 to-accent-500/10',
            iconVar: 'var(--nad-accent-txt)',
          },
          {
            label: 'Active Projects',
            value: String(totalProjects || 24),
            change: dashStats?.data?.projects_delta ?? '+3',
            trending: 'up',
            icon: FolderKanban,
            color: 'from-brand-400/20 to-brand-500/10',
            iconVar: 'var(--nad-info)',
          },
          {
            label: 'Team Members',
            value: String(totalTeam),
            change: `+${totalAdmins} Admins`,
            trending: 'up',
            icon: Users,
            color: 'from-emerald-400/20 to-emerald-500/10',
            iconVar: 'var(--nad-pos)',
          },
          {
            label: 'Present Today',
            value: `${presentCount}/${totalTeam || 156}`,
            change: attendStats?.data?.attendance_rate ? `${attendStats.data.attendance_rate}%` : '99.9%',
            trending: 'up',
            icon: Activity,
            color: 'from-purple-400/20 to-purple-500/10',
            iconVar: '#a78bfa',
          },
        ];

        setStats(liveStats);

        const projList = [];
        if (Array.isArray(ccpProjects?.data) && ccpProjects.data.length) {
          ccpProjects.data.slice(0, 4).forEach((p, idx) => {
            const name = p.project_name || p.account_name || p.title || `Project ${idx + 1}`;
            let progress = 30 + idx * 18;
            let status = 'In Progress';
            let tone = 'info';
            if (p.is_approved_overall === true || p.is_approved_overall === 'true') {
              progress = 92; status = 'Almost Done'; tone = 'accent';
            } else if (p.is_approved_joygie === true || p.is_approved === true) {
              progress = 78; status = 'On Track'; tone = 'pos';
            }
            projList.push({ name, progress, status, tone });
          });
        }
        if (Array.isArray(projectsRes?.data) && projList.length < 4) {
          projectsRes.data.slice(0, 4 - projList.length).forEach((p, idx) => {
            const name = p.title || p.name || `Project ${projList.length + idx + 1}`;
            const progress = p.progress_pct ?? 55;
            const statusMap = { Pending: ['Delayed', 'neg'], Active: ['In Progress', 'info'], Completed: ['Almost Done', 'accent'] };
            const [status, tone] = statusMap[p.status] || ['On Track', 'pos'];
            projList.push({ name, progress, status, tone });
          });
        }
        if (projList.length === 0) {
          projList.push(
            { name: 'Client Portal', progress: 78, status: 'On Track', tone: 'pos' },
            { name: 'Mobile App', progress: 45, status: 'In Progress', tone: 'info' },
            { name: 'Data Migration', progress: 92, status: 'Almost Done', tone: 'accent' },
            { name: 'API Gateway', progress: 30, status: 'Delayed', tone: 'neg' },
          );
        }
        setProjects(projList);

        const actList = [];
        if (Array.isArray(ccpProjects?.data) && ccpProjects.data.length) {
          ccpProjects.data.slice(0, 3).forEach((p, i) => {
            actList.push({
              action: 'Project updated',
              detail: p.project_name || p.account_name || 'Project record',
              time: `${i + 1} hr ago`,
              color: 'bg-brand-500',
            });
          });
        }
        if (payrollStats?.data?.latest_payslip_period) {
          actList.unshift({
            action: 'Payroll processed',
            detail: payrollStats.data.latest_payslip_period,
            time: '2 min ago',
            color: 'bg-accent-500',
          });
        }
        if (Array.isArray(employees?.data) && employees.data.length) {
          const emp = employees.data[employees.data.length - 1];
          actList.unshift({
            action: 'New team member',
            detail: emp.full_name || emp.name || 'New hire added',
            time: '30 min ago',
            color: 'bg-emerald-500',
          });
        }
        if (actList.length < 6) {
          const defaults = [
            { action: 'Deployment completed', detail: 'Production server updated', time: '2 hrs ago', color: 'bg-purple-500' },
            { action: 'Report generated', detail: 'Q4 Financial Summary', time: '3 hrs ago', color: 'bg-rose-500' },
            { action: 'Meeting scheduled', detail: 'Sprint Planning — Team Alpha', time: '4 hrs ago', color: 'bg-cyan-500' },
            { action: 'Invoice sent', detail: 'INV-2024-0847', time: '15 min ago', color: 'bg-accent-500' },
          ];
          let di = 0;
          while (actList.length < 6 && di < defaults.length) {
            const seen = actList.some((a) => a.action === defaults[di].action);
            if (!seen) actList.push(defaults[di]);
            di++;
          }
        }
        setActivity(actList.slice(0, 6));
      } catch (e) {
        console.warn('DashboardPage: failed to fetch live stats — using fallbacks.', e);
      } finally {
        clearTimeout(deadline);
        if (mounted && !deadFired) setLoading(false);
      }
    };

    load();

    return () => { mounted = false; };
  }, [axiosInstance]);

  const fallbackStats = [
    { label: 'Total Revenue', value: '₱2,847,500', change: '+12.5%', trending: 'up', icon: PesoSign, color: 'from-accent-400/20 to-accent-500/10', iconVar: 'var(--nad-accent-txt)' },
    { label: 'Active Projects', value: '24', change: '+3', trending: 'up', icon: FolderKanban, color: 'from-brand-400/20 to-brand-500/10', iconVar: 'var(--nad-info)' },
    { label: 'Team Members', value: '156', change: '+8', trending: 'up', icon: Users, color: 'from-emerald-400/20 to-emerald-500/10', iconVar: 'var(--nad-pos)' },
    { label: 'System Uptime', value: '99.9%', change: '+0.2%', trending: 'up', icon: Activity, color: 'from-purple-400/20 to-purple-500/10', iconVar: '#a78bfa' },
  ];
  const displayStats = stats || fallbackStats;

  const fallbackProjects = [
    { name: 'Client Portal', progress: 78, status: 'On Track', tone: 'pos' },
    { name: 'Mobile App', progress: 45, status: 'In Progress', tone: 'info' },
    { name: 'Data Migration', progress: 92, status: 'Almost Done', tone: 'accent' },
    { name: 'API Gateway', progress: 30, status: 'Delayed', tone: 'neg' },
  ];
  const displayProjects = projects.length ? projects : fallbackProjects;

  const fallbackActivity = [
    { action: 'New project created', detail: 'Client Portal v2.0', time: '2 min ago', color: 'bg-brand-500' },
    { action: 'Invoice sent', detail: 'INV-2024-0847 — ₱125,000', time: '15 min ago', color: 'bg-accent-500' },
    { action: 'Team member joined', detail: 'Maria Santos — Developer', time: '1 hr ago', color: 'bg-emerald-500' },
    { action: 'Deployment completed', detail: 'Production server updated', time: '2 hrs ago', color: 'bg-purple-500' },
    { action: 'Report generated', detail: 'Q4 Financial Summary', time: '3 hrs ago', color: 'bg-rose-500' },
    { action: 'Meeting scheduled', detail: 'Sprint Planning — Team Alpha', time: '4 hrs ago', color: 'bg-cyan-500' },
  ];
  const displayActivity = activity.length ? activity : fallbackActivity;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {displayStats.map((stat, i) => {
          const Icon = stat.icon;
          const s = stagger(i);
          return (
            <div key={stat.label} className={s.className} style={s.style}>
              <Card3D className="content-glass rounded-xl p-5 group h-full">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}
                  >
                    <Icon size={20} style={{ color: stat.iconVar }} />
                  </div>
                  <button
                    className="p-1 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                    style={{ color: 'var(--nad-t4)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--nad-t2)'; e.currentTarget.style.background = 'var(--nad-s1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--nad-t4)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    <MoreHorizontal size={16} />
                  </button>
                </div>
                <p className="text-[11px] font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--nad-t3)' }}>
                  {stat.label}
                </p>
                <div className="flex items-end justify-between">
                  <AnimatedNumber
                    value={stat.value}
                    delay={500 + i * 110}
                    className="text-2xl font-bold"
                    style={{ color: 'var(--nad-t1)' }}
                  />
                  <div
                    className="flex items-center gap-1 text-xs font-semibold"
                    style={{ color: stat.trending === 'up' ? 'var(--nad-pos)' : 'var(--nad-neg)' }}
                  >
                    {stat.trending === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    <AnimatedNumber value={String(stat.change)} delay={700 + i * 110} duration={1100} />
                  </div>
                </div>
                {loading && (
                  <div className="mt-2 h-1 w-full rounded-full overflow-hidden" style={{ background: 'var(--nad-s2)' }}>
                    <div className="h-full w-1/3 animate-pulse rounded-full" style={{ background: 'var(--nad-accent-txt)' }} />
                  </div>
                )}
              </Card3D>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className={`xl:col-span-2 ${stagger(4).className}`} style={stagger(4).style}>
          <Card3D className="content-glass rounded-xl p-5 h-full" intensity={5} lift={10}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold" style={{ color: 'var(--nad-t1)' }}>Active Projects</h3>
              <button
                className="text-[11px] font-medium flex items-center gap-1 transition-colors hover:opacity-80"
                style={{ color: 'var(--nad-accent-txt)' }}
              >
                View All <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="space-y-4">
              {displayProjects.map((project, pi) => (
                <div key={project.name} className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--nad-t2)' }}>{project.name}</p>
                      <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 flex-shrink-0"
                        style={{ color: toneColor[project.tone], background: 'var(--nad-s2)' }}
                      >
                        {project.status}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--nad-s2)' }}>
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-brand-500 to-accent-400"
                        style={{
                          width: `${project.progress}%`,
                          transition: 'width 1.6s cubic-bezier(0.16, 1, 0.3, 1)',
                          transitionDelay: `${600 + pi * 120}ms`,
                        }}
                      />
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--nad-t4)' }}>
                      <AnimatedNumber
                        value={`${project.progress}`}
                        delay={600 + pi * 120}
                        duration={1200}
                      />
                      % completed
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card3D>
        </div>

        <div className={stagger(5).className} style={stagger(5).style}>
          <Card3D className="content-glass rounded-xl p-5 h-full" intensity={5} lift={10}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold" style={{ color: 'var(--nad-t1)' }}>Recent Activity</h3>
              <button
                className="text-[11px] font-medium flex items-center gap-1 transition-colors hover:opacity-80"
                style={{ color: 'var(--nad-accent-txt)' }}
              >
                View All <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="space-y-4">
              {displayActivity.map((item, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${item.color}`} />
                    {idx < displayActivity.length - 1 && (
                      <div className="w-px h-full mt-1" style={{ background: 'var(--nad-b1)' }} />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className="text-xs font-medium" style={{ color: 'var(--nad-t2)' }}>{item.action}</p>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--nad-t3)' }}>{item.detail}</p>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--nad-t4)' }}>{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card3D>
        </div>
      </div>

      <div className={stagger(6).className} style={stagger(6).style}>
        <Card3D className="content-glass rounded-xl p-5" intensity={4} lift={8}>
          <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--nad-t1)' }}>Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'New Project', emoji: '📁' },
              { label: 'Add Member', emoji: '👤' },
              { label: 'Create Invoice', emoji: '📄' },
              { label: 'Generate Report', emoji: '📊' },
            ].map((action) => (
              <button
                key={action.label}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border transition-all group hover:-translate-y-1 hover:shadow-lg"
                style={{
                  background: 'var(--nad-s1)',
                  borderColor: 'var(--nad-b1)',
                  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--nad-s2)';
                  e.currentTarget.style.borderColor = 'var(--nad-b2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--nad-s1)';
                  e.currentTarget.style.borderColor = 'var(--nad-b1)';
                }}
              >
                <span className="text-2xl group-hover:scale-110 transition-transform">
                  {action.emoji}
                </span>
                <span
                  className="text-[11px] font-medium group-hover:transition-colors"
                  style={{ color: 'var(--nad-t3)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--nad-t1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--nad-t3)'; }}
                >
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </Card3D>
      </div>
    </div>
  );
}
